import { useMutation, useQuery } from "@apollo/client/react";
import {
  ArrowPathIcon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { PlusCircleIcon } from "@heroicons/react/24/solid";
import { ColumnDef, getCoreRowModel } from "@tanstack/react-table";
import { Fragment, useCallback, useMemo, useState } from "react";

import { ErrorFallback, Head } from "../../../../components/core";
import { Button } from "../../../../components/form";
import { LimitBy, TableCursor } from "../../../../components/table";
import {
  NotifyType,
  useNotifyContext,
} from "../../../../contexts/NotifyContext";
import {
  type Driver,
  GET_DRIVERS,
  REMOVE_DRIVER,
} from "../../../../graphql/fleets/settings/drivers";
import { AlertModal, AlertType, useAlert } from "../../../../hooks/useAlert";
import { classNames, formatDate } from "../../../../utils";
import { AuthShield } from "../../../auth/core";

const DriverList = ({ breadcrumbs }: { breadcrumbs: Breadcrumb[] }) => {
  const { addNotify } = useNotifyContext();
  const [refetching, setRefetching] = useState<boolean>(false);
  const [pageLimit, setPageLimit] = useState<LimitBy>(LimitBy.L3);

  const { data, loading, error, refetch } = useQuery(GET_DRIVERS);
  const drivers: Driver[] = useMemo<Driver[]>(() => {
    if (data?.fetchDrivers) {
      const fetchedDrivers: Driver[] = data.fetchDrivers.flatMap(
        (item: Driver) => ({
          ...item,
          createdAt: formatDate(item.createdAt),
        })
      );
      return fetchedDrivers;
    }
    return [];
  }, [data]);

  const [deleteDriver] = useMutation(REMOVE_DRIVER, {
    refetchQueries: [
      {
        query: GET_DRIVERS,
      },
    ],
  });
  const handleDelete = async (id: string) => {
    return await deleteDriver({
      variables: {
        id,
      },
    })
      .then(({ data }) => {
        if (data?.driverDelete) {
          const deletedDriver = data.driverDelete?.driver;
          refetch();
          addNotify({
            type: NotifyType.SUCCESS,
            title: `Driver ${deletedDriver.name} deleted`,
          });
        } else {
          addNotify({
            type: NotifyType.ERROR,
            title: "Driver delete failed",
            message: "Something went wrong, please try again later",
          });
        }
      })
      .catch((error) => {
        addNotify({
          type: NotifyType.ERROR,
          title: "Driver delete failed",
          message: error.message,
        });
      });
  };

  type AlertProps = {
    id: string;
    name: string;
  };
  const [alert, setAlert] = useState<AlertProps | null>(null);
  const ResponseAlert = useAlert({
    open: alert ? true : false,
    title: "Do you want to proceed with this operation?",
    message: (
      <p className="text-sm text-gray-500">
        Are you sure you wish to delete{" "}
        <b className="text-gray-900">{alert?.name}</b> permanently. This action
        can't be undone. All records related to{" "}
        <b className="text-gray-900">{alert?.name}</b> will be deleted.
      </p>
    ),
    type: AlertType.DANGER,
    modal: AlertModal.CENTERED_DOUBLE_ACTION,
    delay: 3000,
    onConfirm: async () => {
      await handleDelete(alert?.id!);
      setAlert(null);
    },
    onCancel: () => {
      setAlert(null);
    },
  });

  const columns = useMemo<ColumnDef<Driver, any>[]>(
    () => [
      {
        accessorKey: "id",
        header: "ID",
        size: 30,
        enableHiding: false,
      },
      {
        accessorKey: "name",
        header: "Name",
        enableHiding: false,
        cell: (props) => (
          <span className="whitespace-nowrap">{props.getValue()}</span>
        ),
        accessorFn: (row) => row.name,
      },
      {
        accessorKey: "address",
        header: "Address",
        size: 300,
      },
      {
        accessorKey: "createdAt",
        header: "Created",
        cell: (props) => (
          <span className="whitespace-nowrap">{props.getValue()}</span>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: (info) =>
          info.getValue() ? (
            <span className="inline-flex rounded-full bg-green-100 px-2 text-xs font-medium leading-5 text-green-800">
              Active
            </span>
          ) : (
            <span className="inline-flex rounded-full bg-red-100 px-2 text-xs font-medium leading-5 text-red-800">
              Inactive
            </span>
          ),
      },
      {
        id: "actions",
        header: "Actions",
        cell: (props) => (
          <div className="flex space-x-2 md:space-x-4">
            <AuthShield access={["update-drivers"]}>
              <Button
                type="link"
                variant="icon"
                href={`/fleets/settings/drivers/${props.row.original.id}`}
                className="text-blue-500 hover:text-blue-600"
              >
                <PencilIcon aria-hidden="true" className="text-md h-4 w-4" />
                <span className="sr-only">Edit, {props.row.original.name}</span>
              </Button>
            </AuthShield>
            <AuthShield access={["destroy-drivers"]}>
              <Button
                variant="icon"
                className="text-red-500 hover:text-red-600"
                onClick={() => {
                  setAlert({
                    id: props.row.original.id,
                    name: props.row.original.name,
                  });
                }}
              >
                <TrashIcon aria-hidden="true" className="text-md h-4 w-4" />
                <span className="sr-only">
                  Delete, {props.row.original.name}
                </span>
              </Button>
            </AuthShield>
          </div>
        ),
        enableHiding: false,
        enableSorting: false,
        enableGlobalFilter: false,
        size: 220,
      },
    ],
    []
  );

  const handleRefetch = useCallback(() => {
    setRefetching(true);
    refetch().finally(() => {
      setRefetching(false);
    });
  }, [refetch]);

  if (error) return <ErrorFallback error={error} />;

  return (
    <>
      <Head
        title="Drivers"
        heading="Drivers"
        breadcrumbs={[
          ...breadcrumbs,
          {
            name: "Drivers",
            href: "/fleets/settings/drivers",
          },
          {
            name: DriverListResource.name,
            href: null,
          },
        ]}
      />
      <ResponseAlert />
      <div className="rounded-xl bg-greyish p-5">
        <div className="mb-5 sm:flex sm:items-end">
          <div className="sm:flex-auto">
            <h1 className="text-xl font-medium text-gray-900">
              {DriverListResource.name}
            </h1>
            <p className="mt-2 text-sm text-gray-700">
              {DriverListResource.description}
            </p>
          </div>
          <div className="ml-auto flex items-center space-x-2 pr-3">
            <AuthShield access={["create-drivers"]}>
              <Button type="link" href="/fleets/settings/drivers/add">
                <PlusCircleIcon className="mr-2 h-5 w-5" />
                Add driver
              </Button>
            </AuthShield>
            <Button
              variant="icon"
              onClick={handleRefetch}
              disabled={loading}
              className="flex h-9 items-center justify-center px-2 text-blue-700"
            >
              <ArrowPathIcon
                aria-hidden="true"
                className={classNames(
                  "h-5 w-5",
                  refetching ? "animate-spin" : ""
                )}
              />
              <span className="sr-only">Refresh driver list</span>
            </Button>
          </div>
        </div>

        <TableCursor
          data={drivers}
          columns={columns}
          loading={loading}
          totalRows={drivers.length}
          pageLimit={pageLimit}
          setPageLimit={setPageLimit}
          getCoreRowModel={getCoreRowModel()}
          enableGlobalFilter={true}
          fallbackTitle="No drivers found"
          fallbackText="You can add a new driver by clicking on add driver button."
        />
      </div>
    </>
  );
};

export default DriverList;
export const DriverListResource: ResourceProps = {
  name: "Drivers",
  description: "A list of drivers in the system",
  access: ["read-drivers"],
  path: "drivers",
};
